import { useState, useEffect, useRef, useCallback } from 'react';
import { toaster } from 'evergreen-ui';
import DiaryService from '../../services/diaryService';
import { useAuth } from '../../services/authContext';
import { useRole } from '../../contexts/RoleContext';
import { toLocalSQLDatetime } from '../../utils/timeUtils';
import useWindowSize from '../../utils/useWindowSize';
import { entryMatchesDate, calcClickTime } from './diaryUtils';

export const SLOT_HEIGHT = 48;
export const DAY_HOURS = 24;
export const DAY_HEIGHT = SLOT_HEIGHT * DAY_HOURS;

const EMPTY_FORM = { testo: '', guest_id: '', start_time: '', end_time: '', entry_type: 'appuntamento', notes: [] };

const buildEventForm = (ev) => ({
  testo: ev.type === 'entry' ? (ev.data.testo || '') : (ev.parent?.testo ?? ''),
  guest_id: ev.data?.guest_id ? String(ev.data.guest_id) : (ev.parent?.guest_id ? String(ev.parent.guest_id) : ''),
  start_time: ev.start && !isNaN(ev.start.getTime()) ? ev.start.toISOString() : '',
  end_time: ev.end && !isNaN(ev.end.getTime()) ? ev.end.toISOString() : '',
  entry_type: ev.type === 'entry' ? 'appuntamento' : 'descrizione',
  notes: ev.type === 'note' ? [ev.data] : (ev.parent?.notes ?? []),
});

const buildSubmitPayload = (userId, form) => ({
  user_id: userId, testo: form.testo,
  date: toLocalSQLDatetime(form.start_time) || new Date().toISOString(),
  start_time: form.start_time ? toLocalSQLDatetime(form.start_time) : undefined,
  end_time: form.end_time ? toLocalSQLDatetime(form.end_time) : undefined,
  guest_id: form.guest_id === '' ? null : form.guest_id,
  entry_type: form.entry_type || 'appuntamento',
  notes: form.notes?.length ? form.notes : undefined,
});

const useDiary = ({ guests = [], diaryEntries: propEntries, setDiaryEntries }) => {
  const { effectiveIsDirector: isDirector } = useRole();
  const [entries, setEntries] = useState([]);
  const [sickCollaborators, setSickCollaborators] = useState([]);
  const [injuredCollaborators, setInjuredCollaborators] = useState([]);
  const [futureAppointments, setFutureAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const { getUserId } = useAuth();
  const userId = getUserId && getUserId();
  const containerRef = useRef(null);
  const isMobile = useWindowSize() < 768;
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showDescriptionDialog, setShowDescriptionDialog] = useState(false);
  const [showEditDescriptionDialog, setShowEditDescriptionDialog] = useState(false);
  const [showEditNoteDialog, setShowEditNoteDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const fetchEntries = useCallback(async (date, forceFetch = false) => {
    try {
      if (!forceFetch && propEntries !== undefined && propEntries !== null) {
        const filtered = (propEntries || []).filter(en => entryMatchesDate(en, date));
        setEntries(filtered);
        return filtered;
      }
      if (date) {
        const result = await DiaryService.getEntriesByDate(date);
        const entries = result?.entries ?? result ?? [];
        setEntries(entries);
        setSickCollaborators(result?.sick_collaborators ?? []);
        setInjuredCollaborators(result?.injured_collaborators ?? []);
        return entries;
      }
      const data = await DiaryService.getAllEntries();
      setEntries(data || []);
      setSickCollaborators([]);
      return data || [];
    } catch (error) {
      console.error('Errore durante il recupero delle descrizioni:', error);
      return [];
    }
  }, [propEntries]);

  useEffect(() => {
    (async () => {
      if (propEntries !== undefined && propEntries !== null) { await fetchEntries(selectedDate, true); return; }
      await fetchEntries(selectedDate);
    })();
  }, [selectedDate, propEntries, fetchEntries]);

  const fetchFutureAppointments = useCallback(async () => {
    try {
      const all = await DiaryService.getAllEntries();
      if (!Array.isArray(all)) { setFutureAppointments([]); return []; }
      const now = new Date();
      const future = (all || []).filter(en => en?.entry_type === 'appuntamento' && en.start_time && new Date(en.start_time) > now);
      setFutureAppointments(future);
      return future;
    } catch (err) {
      console.error('Errore fetchFutureAppointments', err);
      setFutureAppointments([]);
      return [];
    }
  }, []);

  useEffect(() => { fetchFutureAppointments(); }, [fetchFutureAppointments]);

  const onEventClick = (ev) => {
    setEditingEntry(ev);
    setForm(buildEventForm(ev));
    const isEntryAuthor = String(ev.data?.user_id) === String(userId);
    if (ev.type === 'entry' && (isDirector || isEntryAuthor)) {
      setShowAddDialog(true);
    } else if (ev.type === 'note') {
      if (isDirector || isEntryAuthor) { setEditingNote(ev); setShowEditNoteDialog(true); }
      else { setShowViewDialog(true); }
    } else {
      setShowViewDialog(true);
    }
  };

  const onEmptyDoubleClick = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const dt = calcClickTime(e.clientY - rect.top, selectedDate, DAY_HEIGHT);
    const endDt = new Date(dt.getTime() + 30 * 60000);
    setForm({ testo: '', guest_id: '', start_time: dt.toISOString(), end_time: endDt.toISOString(), entry_type: dt > new Date() ? 'appuntamento' : 'descrizione', notes: [] });
    setEditingEntry({ start: dt.toISOString(), end: endDt.toISOString(), entry_type: dt > new Date() ? 'appuntamento' : 'descrizione' });
    if (dt > new Date()) { setShowAddDialog(true); } else { setShowDescriptionDialog(true); }
  };

  const handleAddSubmit = async (payloadOverride) => {
    try {
      const payload = payloadOverride ?? buildSubmitPayload(userId, form);
      const isUpdate = !!payload.id;
      if (isUpdate) { await DiaryService.updateEntry(payload); }
      else { await DiaryService.addEntry(payload); }
      toaster.success(isUpdate ? 'Voce aggiornata' : 'Voce aggiunta con successo');
      setShowAddDialog(false);
      setShowDescriptionDialog(false);
      const fresh = await fetchEntries(selectedDate, true);
      if (setDiaryEntries) setDiaryEntries(fresh || []);
      await fetchFutureAppointments();
    } catch (err) {
      console.error(err);
      toaster.danger('Errore durante il salvataggio');
    }
  };

  const handleDelete = async (id) => {
    try {
      await DiaryService.deleteEntry(id);
      toaster.success('Voce eliminata');
      setShowAddDialog(false);
      const fresh = await fetchEntries(selectedDate, true);
      if (setDiaryEntries) setDiaryEntries(fresh || []);
      await fetchFutureAppointments();
    } catch (err) {
      console.error(err);
      toaster.danger("Errore durante l'eliminazione");
    }
  };

  return {
    entries, sickCollaborators, injuredCollaborators, futureAppointments, selectedDate, setSelectedDate,
    userId, containerRef, isMobile, form,
    editingEntry, setEditingEntry, editingNote, setEditingNote,
    showAddDialog, setShowAddDialog,
    showViewDialog, setShowViewDialog,
    showDescriptionDialog, setShowDescriptionDialog,
    showEditDescriptionDialog, setShowEditDescriptionDialog,
    showEditNoteDialog, setShowEditNoteDialog,
    fetchEntries, onEventClick, onEmptyDoubleClick, handleAddSubmit, handleDelete,
  };
};

export default useDiary;
